<template>
  <div class="dossier-edit">
    <NavBar />
    <AuthBySessionExpired />

    <!-- ─── Barra de controles ─── -->
    <div class="dossier-edit__topbar">
      <div class="dossier-edit__topbar-left">
        <!-- Botão voltar: fecha módulo ativo OU navega para página anterior -->
        <button
          class="dossier-edit__back-btn"
          type="button"
          @click="handleBack"
        >
          <i class="fas fa-chevron-left"></i>
          <span>{{ activeModule ? moduleLabel : "Voltar" }}</span>
        </button>

        <!-- Breadcrumb: Dossiê > Nome do módulo -->
        <div class="dossier-edit__breadcrumb">
          <span
            class="dossier-edit__breadcrumb-root"
            :class="{ 'dossier-edit__breadcrumb-root--link': !!activeModule }"
            @click="activeModule ? closeModule() : null"
          >
            <i class="fas fa-folder-open mr-1"></i>
            Dossiê
          </span>
          <template v-if="activeModule">
            <i class="fas fa-chevron-right dossier-edit__breadcrumb-sep"></i>
            <span class="dossier-edit__breadcrumb-current">
              {{ moduleLabel }}
            </span>
          </template>
        </div>
      </div>

      <div class="dossier-edit__topbar-right">
        <!-- Botão para a visão completa (modo view) -->
        <v-btn
          small
          class="btn-add-down"
          @click="$router.push({ name: 'dossier' })"
        >
          <i class="fas fa-eye mr-1"></i>
          Visualizar Dossiê
        </v-btn>
      </div>
    </div>

    <!-- ─── Loading inicial ─── -->
    <b-overlay :show="loading" opacity="0.99">
      <div class="dossier-edit__body">
        <!-- ══════════════════════════════════════════
             ÍNDICE (visível quando nenhum módulo está aberto)
             ══════════════════════════════════════════ -->
        <transition name="dossier-edit__fade">
          <DossierIndex
            v-if="!activeModule"
            :summary-data="summaryData"
            @navigate="openModule"
          />
        </transition>

        <!-- ══════════════════════════════════════════
             MÓDULOS DOCUMENTAIS
             Cada um recebe mode="edit" e os dados iniciais
             carregados do /full. Ao salvar, atualiza o cache
             local e volta ao índice.
             ══════════════════════════════════════════ -->

        <!-- CNPJ -->
        <transition name="dossier-edit__slide">
          <DossierCnpj
            v-if="activeModule === 'cnpj'"
            mode="edit"
            :initial-data="modulesData.cnpj"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Responsável Técnico -->
        <transition name="dossier-edit__slide">
          <DossierTechnicalManager
            v-if="activeModule === 'technical_manager'"
            mode="edit"
            :initial-data="modulesData.technical_manager"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Alvará -->
        <transition name="dossier-edit__slide">
          <DossierLicense
            v-if="activeModule === 'license'"
            mode="edit"
            :initial-data="modulesData.license"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Licença Sanitária -->
        <transition name="dossier-edit__slide">
          <DossierSanitaryLicense
            v-if="activeModule === 'sanitary_license'"
            mode="edit"
            :initial-data="modulesData.sanitary_license"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Termo de Visita Sanitária -->
        <transition name="dossier-edit__slide">
          <DossierSanitaryVisit
            v-if="activeModule === 'sanitary_visit'"
            mode="edit"
            :initial-data="modulesData.sanitary_visit"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Corpo de Bombeiros -->
        <transition name="dossier-edit__slide">
          <DossierFireDept
            v-if="activeModule === 'fire_dept'"
            mode="edit"
            :initial-data="modulesData.fire_dept"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- CMI / CMDPI -->
        <transition name="dossier-edit__slide">
          <DossierElderlyCouncil
            v-if="activeModule === 'elderly_council'"
            mode="edit"
            :initial-data="modulesData.elderly_council"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- CMAS -->
        <transition name="dossier-edit__slide">
          <DossierWelfareCouncil
            v-if="activeModule === 'welfare_council'"
            mode="edit"
            :initial-data="modulesData.welfare_council"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Reservatórios de Água -->
        <transition name="dossier-edit__slide">
          <DossierWaterTank
            v-if="activeModule === 'water_tank'"
            mode="edit"
            :initial-data="modulesData.water_tank"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Potabilidade da Água -->
        <transition name="dossier-edit__slide">
          <DossierWaterPotability
            v-if="activeModule === 'water_potability'"
            mode="edit"
            :initial-data="modulesData.water_potability"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Controle de Pragas -->
        <transition name="dossier-edit__slide">
          <DossierPestControl
            v-if="activeModule === 'pest_control'"
            mode="edit"
            :initial-data="modulesData.pest_control"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Terceirização -->
        <transition name="dossier-edit__slide">
          <DossierOutsourcing
            v-if="activeModule === 'outsourcing'"
            mode="edit"
            :initial-data="modulesData.outsourcing"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Prestadores de Saúde -->
        <transition name="dossier-edit__slide">
          <DossierHealthProviders
            v-if="activeModule === 'health_providers'"
            mode="edit"
            :initial-data="modulesData.health_providers"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- RH – Colaboradores CLT -->
        <transition name="dossier-edit__slide">
          <DossierHRClt
            v-if="activeModule === 'hr_clt'"
            mode="edit"
            :initial-data="modulesData.hr_clt"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- Infraestrutura -->
        <transition name="dossier-edit__slide">
          <DossierInfrastructure
            v-if="activeModule === 'infrastructure'"
            mode="edit"
            :initial-data="modulesData.infrastructure"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- POPs -->
        <transition name="dossier-edit__slide">
          <DossierSOPs
            v-if="activeModule === 'sops'"
            mode="edit"
            :initial-data="modulesData.sops"
            :start-collapsed="false"
            @saved="onSaved"
            @error="onError"
          />
        </transition>

        <!-- ══════════════════════════════════════════
             MÓDULOS OPERACIONAIS
             Não usam modo edit/view — são formulários
             independentes que gerenciam estado próprio.
             ══════════════════════════════════════════ -->

        <!-- Escalas de Trabalho -->
        <transition name="dossier-edit__slide">
          <DossierWorkSchedule v-if="activeModule === 'work_schedule'" />
        </transition>

        <!-- Cardápios -->
        <transition name="dossier-edit__slide">
          <DossierMenu v-if="activeModule === 'menu'" />
        </transition>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import NavBar from "../../templates/NavBar";
import AuthBySessionExpired from "../../templates/Components/AuthBySessionExpired";

// ── Módulos documentais ────────────────────────────────────────────────────
import DossierIndex from "../../components/dossier/DossierIndex.vue";
import DossierCnpj from "../../components/modules/DossierCnpj.vue";
import DossierTechnicalManager from "../../components/modules/DossierTechnicalManager.vue";
import DossierLicense from "../../components/modules/DossierLicense.vue";
import DossierSanitaryLicense from "../../components/modules/DossierSanitaryLicense.vue";
import DossierSanitaryVisit from "../../components/modules/DossierSanitaryVisit.vue";
import DossierFireDept from "../../components/modules/DossierFireDept.vue";
import DossierElderlyCouncil from "../../components/modules/DossierElderlyCouncil.vue";
import DossierWelfareCouncil from "../../components/modules/DossierWelfareCouncil.vue";
import DossierWaterTank from "../../components/modules/DossierWaterTank.vue";
import DossierWaterPotability from "../../components/modules/DossierWaterPotability.vue";
import DossierPestControl from "../../components/modules/DossierPestControl.vue";
import DossierOutsourcing from "../../components/modules/DossierOutsourcing.vue";
import DossierHealthProviders from "../../components/modules/DossierHealthProviders.vue";
import DossierHRClt from "../../components/modules/DossierHR.vue";
import DossierInfrastructure from "../../components/modules/DossierInfrastructure.vue";
import DossierSOPs from "../../components/modules/DossierSOPs.vue";

// ── Módulos operacionais ───────────────────────────────────────────────────
import DossierWorkSchedule from "../../components/operational/DossierWorkSchedule.vue";
import DossierMenu from "../../components/operational/DossierMenu.vue";

import api from "../../services/api";
import toast from "../../lib/ToastNotification";

// ─── Mapa de chaves → labels legíveis ──────────────────────────────────────
// Usado no breadcrumb e no botão Voltar quando um módulo está aberto.
const MODULE_LABELS = {
  cnpj: "CNPJ",
  technical_manager: "Responsável Técnico",
  license: "Alvará de Funcionamento",
  sanitary_license: "Licença Sanitária",
  sanitary_visit: "Termo de Visita Sanitária",
  fire_dept: "Corpo de Bombeiros",
  elderly_council: "Conselho do Idoso (CMI)",
  welfare_council: "Conselho de Assistência (CMAS)",
  water_tank: "Limpeza dos Reservatórios",
  water_potability: "Laudo de Potabilidade",
  pest_control: "Controle de Pragas",
  outsourcing: "Terceirização de Serviços",
  health_providers: "Prestadores de Saúde",
  hr_clt: "Colaboradores CLT",
  infrastructure: "Infraestrutura",
  sops: "POPs",
  work_schedule: "Escalas de Trabalho",
  menu: "Cardápios",
};

export default {
  name: "DossierEdit",

  components: {
    NavBar,
    AuthBySessionExpired,
    DossierIndex,
    DossierCnpj,
    DossierTechnicalManager,
    DossierLicense,
    DossierSanitaryLicense,
    DossierSanitaryVisit,
    DossierFireDept,
    DossierElderlyCouncil,
    DossierWelfareCouncil,
    DossierWaterTank,
    DossierWaterPotability,
    DossierPestControl,
    DossierOutsourcing,
    DossierHealthProviders,
    DossierHRClt,
    DossierInfrastructure,
    DossierSOPs,
    DossierWorkSchedule,
    DossierMenu,
  },

  data() {
    return {
      // Módulo atualmente aberto. null = exibe o DossierIndex.
      activeModule: null,

      // Dados completos carregados do GET /dossier/full.
      // Cada módulo recebe sua fatia via :initial-data.
      modulesData: {},

      // Dados de resumo repassados ao DossierIndex para evitar duplo fetch.
      // Populados pelo /summary — mais leve que o /full.
      summaryData: null,

      loading: false,
    };
  },

  computed: {
    // Label do módulo ativo para o breadcrumb e botão voltar
    moduleLabel() {
      return MODULE_LABELS[this.activeModule] || this.activeModule;
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    // ── Carrega dados em paralelo ──────────────────────────────────────────
    // /summary → alimenta o DossierIndex imediatamente (rápido)
    // /full    → pré-carrega os dados para quando o usuário abrir um módulo
    async retrieve() {
      this.loading = true;

      // Busca summary e full em paralelo — não bloqueia um pelo outro
      const [summaryRes, fullRes] = await Promise.allSettled([
        api.get("/rest-home/dossier/summary"),
        api.get("/rest-home/dossier/full"),
      ]);

      if (summaryRes.status === "fulfilled" && !summaryRes.value.data?.error) {
        this.summaryData = summaryRes.value.data?.data || null;
      }

      if (fullRes.status === "fulfilled" && !fullRes.value.data?.error) {
        this.modulesData = fullRes.value.data?.data || {};
      }

      this.loading = false;
    },

    // ── Abre um módulo ─────────────────────────────────────────────────────
    openModule(key) {
      this.activeModule = key;
      // Scroll ao topo para que o usuário veja o início do formulário
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // ── Fecha o módulo ativo e volta ao índice ──────────────────────────────
    closeModule() {
      this.activeModule = null;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // ── Trata o clique no botão Voltar ─────────────────────────────────────
    // Se há módulo aberto → fecha e volta ao índice.
    // Se está no índice   → navega para a página anterior no histórico.
    handleBack() {
      if (this.activeModule) {
        this.closeModule();
      } else {
        this.$router.go(-1);
      }
    },

    // ── Callback de sucesso ao salvar um módulo ────────────────────────────
    // Atualiza o cache local de modulesData e summaryData para que o índice
    // reflita o novo estado sem re-fetch.
    async onSaved({ module, data }) {
      module;
      data;
      // Atualiza cache do /full (dados completos do módulo)
      // if (data) {
      //   this.$set(this.modulesData, module, {
      //     ...(this.modulesData[module] || {}),
      //     ...data,
      //   });
      // }

      // // Atualiza cache do /summary (status + validity_date + not_applicable)
      // // com os campos que o DossierIndex usa para calcular o estado do card.
      // if (data && this.summaryData) {
      //   const SUMMARY_KEYS = [
      //     "status",
      //     "validity_date",
      //     "not_applicable",
      //     "file_url",
      //   ];
      //   const patch = {};
      //   SUMMARY_KEYS.forEach((k) => {
      //     if (data[k] !== undefined) patch[k] = data[k];
      //   });
      //   this.$set(this.summaryData, module, {
      //     ...(this.summaryData[module] || {}),
      //     ...patch,
      //   });
      // }

      // Volta ao índice após salvar com sucesso
      await this.retrieve();
      this.closeModule();
    },

    // ── Callback de erro ao salvar ─────────────────────────────────────────
    // Permanece no módulo para que o usuário corrija.
    onError({ module, errors }) {
      const first = errors?.[0]?.message || "Erro ao salvar.";
      toast(`${this.moduleLabel}: ${first}`, "error");
      console.error("DossierEdit.onError:", module, errors);
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER EDIT
   Tela de edição do Dossiê. Gerencia a navegação interna entre
   o DossierIndex e os módulos individuais.
   ═══════════════════════════════════════════════════════════════ */

.dossier-edit {
  min-height: 100vh;
  background: #f0f0ef;
}

/* ── Barra de controles (sticky) ─────────────────────────────── */
.dossier-edit__topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 2px solid #1f5a76;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}

.dossier-edit__topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* ── Botão voltar ────────────────────────────────────────────── */
.dossier-edit__back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  white-space: nowrap;
  /* Toque mínimo de 44px (Lei de Fitts / WCAG 2.5.5) */
  min-height: 32px;
}

.dossier-edit__back-btn:hover {
  border-color: #1f5a76;
  color: #1f5a76;
  background: #f0f7fb;
}

.dossier-edit__back-btn i {
  font-size: 10px;
}

/* ── Breadcrumb ──────────────────────────────────────────────── */
.dossier-edit__breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #888;
}

.dossier-edit__breadcrumb-root {
  color: #888;
  font-weight: 500;
}

.dossier-edit__breadcrumb-root--link {
  color: #1f5a76;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.dossier-edit__breadcrumb-root--link:hover {
  color: #174a60;
}

.dossier-edit__breadcrumb-sep {
  font-size: 9px;
  color: #bbb;
}

.dossier-edit__breadcrumb-current {
  font-size: 13px;
  font-weight: 600;
  color: #33495e;
}

/* ── Corpo da tela ───────────────────────────────────────────── */
.dossier-edit__body {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Transição: fade para o índice ───────────────────────────── */
.dossier-edit__fade-enter-active,
.dossier-edit__fade-leave-active {
  transition: opacity 0.2s ease;
}
.dossier-edit__fade-enter,
.dossier-edit__fade-leave-to {
  opacity: 0;
}

/* ── Transição: slide para os módulos ────────────────────────── */
/* Entra da direita; sai para a esquerda — simula drill-down. */
.dossier-edit__slide-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.dossier-edit__slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
  position: absolute; /* remove do fluxo durante a saída */
  width: 100%;
}
.dossier-edit__slide-enter {
  opacity: 0;
  transform: translateX(24px);
}
.dossier-edit__slide-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 768px) {
  .dossier-edit__topbar {
    position: static; /* remove sticky em mobile para evitar sobreposição */
    flex-wrap: wrap;
  }

  .dossier-edit__topbar-left {
    flex: 1;
    min-width: 0;
  }

  .dossier-edit__breadcrumb {
    display: none; /* breadcrumb redundante com o botão voltar em mobile */
  }

  .dossier-edit__body {
    padding: 12px 10px 40px;
  }
}
</style>